import {useSetAtom} from "jotai";
import {useLoadableValueOrDefault} from "./jotaiHooks";
import {Order} from "../types/domain/order";
import {useFetchItems} from "./useFetchItems";
import {useResourceManagement} from "./resourceHooks";
import {
    addFields,
    changeStatus,
    createOrder,
    deleteOrder,
    editOrder,
    finalizeOrder,
    changeFlags,
    getOrdersByCompany,
    downloadShapeFile,
} from "../services/orderService";
import {OrderStatus} from "../types/domain/orderStatus";
import {PatchOrderBodyType, PostOrderBodyType} from "../types/domain/order";
import {OrderFlags} from "../types/domain/orderFlags";
import {activeCompanyOrdersAtom, activeCompanyOrdersLoadable} from "../store/activeCompanyOrdersStore";

export const useOrders = (companyId: string | null) => {
    return useFetchItems<Order>(companyId, getOrdersByCompany);
};

export const useActiveCompanyOrders = () => {
    const refresh = useSetAtom(activeCompanyOrdersAtom);
    const items = useLoadableValueOrDefault(activeCompanyOrdersLoadable, [], (atom) => atom.orders.sort(sortOrders));
    const mapByFieldId = useLoadableValueOrDefault(activeCompanyOrdersLoadable, {}, (atom) => atom.mapByOrderId);
    return {items, mapByFieldId, refresh};
};

function sortOrders(a: Order, b: Order): number {
    if (a.sid && b.sid) {
        return a.sid < b.sid ? -1 : a.sid > b.sid ? 1 : 0;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export const useOrderManagement = () =>
    useResourceManagement({
        get: (companyId: string) => getOrdersByCompany(companyId),
        create: (companyId: string, props: PostOrderBodyType) => createOrder(companyId, props),
        edit: (companyId: string, id: string, props: PatchOrderBodyType) => editOrder(companyId, id, props),
        finalize: (companyId: string, id: string) => finalizeOrder(companyId, id),
        addFields: (companyId: string, orderId: string, props: {fieldIds: string[]}) =>
            addFields(companyId, orderId, props),
        changeStatus: (companyId: string, orderId: string, newStatus: OrderStatus) =>
            changeStatus(companyId, orderId, newStatus),
        changeFlags: (companyId: string, orderId: string, flags: OrderFlags[]) =>
            changeFlags(companyId, orderId, flags),
        delete: (companyId: string, id: string) => deleteOrder(companyId, id),
        downloadShapeFile: (companyId: string, orderId: string) => downloadShapeFile(companyId, orderId),
    });
